/* Shopify's documented storefront DOM events, not Web Pixel standard-event publishing.
 * Reference: https://shopify.dev/docs/api/storefront-events-and-actions/events/dispatch
 * Never delays or retries a cart write. Missing SDK means a no-op for that operation.
 */
(() => {
  'use strict';
  let sdk = null;
  const idle = Object.freeze({ active: false, resolve: () => false, reject: () => false });
  const actions = new Set(['add', 'update', 'remove']);
  const contexts = new Set(['product', 'cart', 'dialog']);

  function eventTarget(context, target) {
    if (target && typeof target.dispatchEvent === 'function' && target.isConnected !== false) return target;
    if (context === 'dialog') return document.querySelector('#cart-dialog[open]') || document;
    if (context === 'cart') return document.querySelector('.cart-page') || document;
    return document.querySelector('.pdp') || document;
  }

  function start({ action, context, lines, target } = {}) {
    if (!sdk || !actions.has(action) || !contexts.has(context) || !Array.isArray(lines) || !lines.length) return idle;
    const identifier = action === 'add' ? 'merchandiseId' : 'id';
    if (lines.some(line => !line || line[identifier] == null || String(line[identifier]).length === 0 ||
      !Number.isSafeInteger(line.quantity) || line.quantity < (action === 'add' ? 1 : 0))) return idle;
    let deferred;
    let emitted;
    let node;
    try {
      deferred = sdk.CartLinesUpdateEvent.createPromise();
      // The SDK transforms the promise, creating a second rejection chain.
      deferred.promise.catch(() => {});
      emitted = new sdk.CartLinesUpdateEvent({
        action,
        context,
        lines: lines.map(line => ({ [identifier]: line[identifier], quantity: line.quantity })),
        promise: deferred.promise
      });
      emitted.promise.catch(() => {});
      node = eventTarget(context, target);
      node.dispatchEvent(emitted);
    } catch (_) {
      deferred?.reject(new Error('Cart event unavailable.'));
      return idle;
    }

    let settled = false;
    let timeout;
    function reject() {
      if (settled) return false;
      settled = true;
      clearTimeout(timeout);
      deferred.reject(new Error('Unable to confirm this cart update.'));
      try {
        node.dispatchEvent(new sdk.CartErrorEvent({
          error: 'Unable to confirm this cart update.',
          code: 'SERVICE_UNAVAILABLE'
        }));
      } catch (_) { /* An analytics integration must never block commerce. */ }
      return true;
    }
    timeout = setTimeout(reject, 45000);

    return {
      active: true,
      // Optional userErrors/warnings let a known cart decline resolve honestly.
      // A transport/ambiguous-write failure must reject; never report success.
      resolve(cart, result = {}) {
        if (settled) return false;
        try {
          if (!cart || typeof cart.currency !== 'string' || !Array.isArray(cart.items) ||
            !Number.isSafeInteger(cart.item_count) || !Number.isFinite(cart.total_price) || !cart.token) return reject();
          const converted = sdk.CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
          settled = true;
          clearTimeout(timeout);
          deferred.resolve({
            cart: converted,
            ...(Array.isArray(result.userErrors) ? { userErrors: result.userErrors } : {}),
            ...(Array.isArray(result.warnings) ? { warnings: result.warnings } : {})
          });
          return true;
        } catch (_) {
          return reject();
        }
      },
      reject
    };
  }

  const bridge = { start, isReady: () => Boolean(sdk) };
  window.ToscaStandardEventsBridge = bridge;
  bridge.ready = import('https://cdn.shopify.com/storefront/standard-events.js')
    .then(module => {
      if (typeof module.CartLinesUpdateEvent !== 'function' ||
          typeof module.CartLinesUpdateEvent.createPromise !== 'function' ||
          typeof module.CartLinesUpdateEvent.createCartFromAjaxResponse !== 'function' ||
          typeof module.CartErrorEvent !== 'function') return false;
      sdk = module;
      return true;
    })
    .catch(() => false);
})();
